<script setup lang="ts" generic="T extends 'default' | 'select', U">

import { type UnwrapRef } from 'vue';

import { computed, reactive, watch } from '#imports';

type RangeType = 'default' | 'select';

type ValueType = {
    min?: string;
    max?: string;
    optionValue?: OptionValue<U>['value'];
};

const props = withDefaults(
    defineProps<{
        isLoading?: boolean;
        type?: RangeType;
        selectItems?: OptionValue<U>[];
        selectValue: ValueType;
        title?: string;
    }>(),
    {
        selectItems: undefined,
        title: undefined,
        type: 'default'
    }
);

const selectedValue = reactive<ValueType>({
    min: props.selectValue.min ?? '',
    max: props.selectValue.max ?? '',
    optionValue: props.selectValue.optionValue
});

const emit = defineEmits<{
    (e: 'onChange', payload: ValueType): void;
}>();

const computedSelectedValue = computed(() => props.selectItems?.find(item => item.value === props.selectValue.optionValue));

watch(
    () => props.selectValue,
    (newValue) => {
        selectedValue.min = newValue.min ?? '';
        selectedValue.max = newValue.max ?? '';
        selectedValue.optionValue = newValue.optionValue as UnwrapRef<U> | undefined;
    }
);

watch(
    selectedValue,
    (newValue) => {
        emit(
            'onChange',
            {
                min: newValue.min,
                max: newValue.max,
                optionValue: newValue.optionValue as U | undefined
            }
        );
    }
);

const onChangeSelect = (val: OptionValue<U>) => {
    selectedValue.optionValue = val.value as UnwrapRef<U> | undefined;
};

</script>

<template>
  <div class="display-flex blocks flex-column pad-24 gap-12">
    <p v-if="title"
       class="compact-500-accent"
    >
      {{ title }}
    </p>
    <div class="display-flex gap-4 width-full">
      <div class="display-flex align-items-center gap-4 width-full">
        <div class="display-flex flex-column align-items-start gap-4 width-full">
          <input v-model.lazy.trim="selectedValue.min"
                 placeholder="Min"
                 aria-label="min range price"
                 class="base-input compact-400-default"
                 :disabled="props.isLoading"
                 name="minInput"
                 type="number"
          >
        </div>

        <div class="display-flex flex-column align-items-start gap-4 width-full">
          <input v-model.lazy.trim="selectedValue.max"
                 placeholder="Max"
                 aria-label="max range price"
                 class="base-input compact-400-default"
                 :disabled="props.isLoading"
                 name="maxInput"
                 type="number"
          >
        </div>
        <template v-if="type === 'select' && selectItems">
          <div v-if="isLoading">
            <LoaderSkeleton :height="40"
                            :width="80"
            />
          </div>
          <FormSelect v-else
                      track-by="label"
                      label="label"
                      :is-disabled="selectItems.length <= 1"
                      container-class="select"
                      :initial-value="computedSelectedValue || selectItems[0]"
                      :options="selectItems"
                      @on-change="(payload: OptionValue<U>) => onChangeSelect(payload)"
          />
        </template>
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select {
  max-width: 85px;
}

</style>
